import { createContext, useContext, useMemo } from "react";

/** 目前支援的語系。未來要加語系就擴充這裡。 */
export type SupportedLocale = "zh-TW" | "en";

/** 預設語系（找不到對應語系時的 fallback）。 */
export const DEFAULT_LOCALE: SupportedLocale = "zh-TW";

/** 一份語系字典：key → 文案字串。 */
export type LocaleMessages = Record<string, string>;

/** 多語系字典：locale → 該語系的字典。 */
export type LocaleDictionary = Partial<Record<SupportedLocale, LocaleMessages>>;

interface LocaleContextValue {
	/** 目前語系 */
	locale: SupportedLocale;
	/** 取文案：找不到 key 時回傳 key 本身，方便開發時看出漏翻。 */
	t: (key: string) => string;
	/** 目前語系的完整字典（需要整包時用） */
	messages: LocaleMessages;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

interface LocaleProviderProps {
	/** 目前語系（由外殼依 LIFF / URL 參數決定後傳入） */
	locale: SupportedLocale;
	/** 該頁 / 該活動的多語系字典 */
	dictionary: LocaleDictionary;
	/** 找不到目前語系時要退回的語系，預設 en */
	fallbackLocale?: SupportedLocale;
	children: React.ReactNode;
}

/**
 * 輕量多語系 Provider（不依賴任何 i18n 套件）。
 * 外殼決定好 locale 與字典後包住子樹，子元件用 useLocale() 取 t / locale。
 */
export function LocaleProvider({
	locale,
	dictionary,
	fallbackLocale = DEFAULT_LOCALE,
	children,
}: LocaleProviderProps) {
	const value = useMemo<LocaleContextValue>(() => {
		const messages =
			dictionary[locale] ?? dictionary[fallbackLocale] ?? {};
		const t = (key: string) => messages[key] ?? key;
		return { locale, t, messages };
	}, [locale, dictionary, fallbackLocale]);

	return (
		<LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
	);
}

/** 在 LocaleProvider 內取得 { locale, t, messages }。 */
export function useLocale(): LocaleContextValue {
	const ctx = useContext(LocaleContext);
	if (!ctx) {
		throw new Error("useLocale 必須在 <LocaleProvider> 內使用");
	}
	return ctx;
}
